import {
  Account,
  AccountTypeEnum,
  SubmitPushToCardTransferResult,
  SubmitPushToCardTransferResultTypenames,
  SubmitTransferResult,
  SubmitTransferResultTypenames,
  SubmitTransferSuccess,
  TransferDestinationInput,
  TransferRail,
  TransferSourceInput,
} from '@varo/graphql-schema-types';
import { TRANSFER_MONEY_FLOW_ERROR_ENCOUNTERED } from 'shared/analytics/events/transferMoney';
import type { FocusTrackAnalyticsProps } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { classifyError } from 'shared/utils/errors/classifyError';
import { CombinedError, OperationResult } from 'urql';

export interface ReviewedTransfer {
  source: TransferSourceInput;
  destination: TransferDestinationInput;
  amount: string;
  currency: string;
}

export interface TransferSuccessfulAttributes {
  from_account_id: string;
  to_account_id: string;
  transfer_amount: string;
  transfer_type: string;
  from_account_type?: string;
  to_account_type?: string;
  from_bank?: string;
  to_bank?: string;
  transfer_id?: string;
}

export const TRANSFER_SUBMIT_STEP_NAME = 'Transfer Money Request Submitted';

export const UNKNOWN_ERROR_REASON = 'unknown';

export const PUSH_TO_CARD_TRANSFER_TYPE = 'external_debit_push';

export const formatErrorReason = (code?: string) =>
  code?.toLowerCase() ?? UNKNOWN_ERROR_REASON;

export const flowNameAttributes = (
  flowName?: string,
): Record<string, string> => (flowName ? { flow_name: flowName } : {});

export const transferFlowErrorEvent = (
  sourceName: string,
  errorReason: string,
  flowName?: string,
): FocusTrackAnalyticsProps => ({
  name: TRANSFER_MONEY_FLOW_ERROR_ENCOUNTERED.key,
  customAttributes: {
    ...TRANSFER_MONEY_FLOW_ERROR_ENCOUNTERED.properties,
    source_name: sourceName,
    step_name: TRANSFER_SUBMIT_STEP_NAME,
    error_reason: errorReason,
    ...flowNameAttributes(flowName),
  },
});

const accountTypeOf = (account?: Pick<Account, 'type' | 'product'>) =>
  (account?.type === AccountTypeEnum.Internal
    ? account.product
    : account?.type
  )?.toLowerCase();

const accountTransferType = (
  rail: TransferRail,
  sourceAccount?: Pick<Account, 'type'>,
) => {
  if (rail === TransferRail.Internal) {
    return 'internal';
  }
  return sourceAccount?.type === AccountTypeEnum.External
    ? 'ach_pull'
    : 'ach_push';
};

// The analytics payload must never throw after money has moved, so server fields are read null-safely.
export const accountTransferAttributes = (
  reviewed: ReviewedTransfer,
  rail: TransferRail,
  { id, transfer }: SubmitTransferSuccess,
): TransferSuccessfulAttributes => ({
  from_account_id: reviewed.source.id,
  to_account_id: reviewed.destination.id,
  from_account_type: accountTypeOf(transfer?.sourceAccount),
  to_account_type: accountTypeOf(transfer?.destinationAccount),
  from_bank: transfer?.sourceAccount?.institution?.name?.toLowerCase(),
  to_bank: transfer?.destinationAccount?.institution?.name?.toLowerCase(),
  transfer_amount: reviewed.amount,
  transfer_type: accountTransferType(rail, transfer?.sourceAccount),
  transfer_id: id,
});

const errorMessage = (code: string, message: string) => `${code}: ${message}`;

export const rejectionMessage = (
  result: SubmitTransferResult | SubmitPushToCardTransferResult | undefined,
) => {
  switch (result?.__typename) {
    case SubmitTransferResultTypenames.SubmitTransferError:
      return errorMessage(formatErrorReason(result.code), result.message);
    case SubmitPushToCardTransferResultTypenames.SubmitPushToCardTransferError:
      return errorMessage(formatErrorReason(result.errorCode), result.message);
    default:
      return undefined;
  }
};

export const operationErrorMessage = (error: CombinedError) =>
  errorMessage(classifyError(error).type, error.message);

export const describeCause = (cause: unknown) => {
  if (cause instanceof Error) {
    return cause.message;
  }
  return typeof cause === 'string' ? cause : undefined;
};

export const correlationIdOf = (
  extensions: OperationResult['extensions'],
  error?: CombinedError,
) => {
  const correlationId =
    extensions?.correlationId ??
    error?.graphQLErrors.find(
      (graphQLError) => graphQLError.extensions?.correlationId !== undefined,
    )?.extensions?.correlationId;
  return typeof correlationId === 'string' ? correlationId : undefined;
};
